import React, { useState, useEffect, useCallback } from 'react';
import '../assets/styles/Gallery.scss';

export interface GalleryPhoto {
    src: string;
    alt: string;
}

interface GalleryProps {
    photos: GalleryPhoto[];
}

function Gallery({ photos }: GalleryProps) {
    const [lightboxIndex, setLightboxIndex] = useState(-1);

    const isOpen = lightboxIndex >= 0;
    const hasPrev = lightboxIndex > 0;
    const hasNext = lightboxIndex < photos.length - 1;

    const close = useCallback(() => setLightboxIndex(-1), []);
    const prev = useCallback(() => setLightboxIndex((i) => i - 1), []);
    const next = useCallback(() => setLightboxIndex((i) => i + 1), []);

    useEffect(() => {
        if (!isOpen) return;
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') close();
            if (e.key === 'ArrowLeft' && hasPrev) prev();
            if (e.key === 'ArrowRight' && hasNext) next();
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [isOpen, hasPrev, hasNext, close, prev, next]);

    // Prevent body scroll when lightbox is open
    useEffect(() => {
        document.body.style.overflow = isOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    return (
        <>
            <div className="gallery-masonry">
                {photos.map((photo, i) => (
                    <div
                        key={i}
                        className="gallery-item"
                        onClick={() => setLightboxIndex(i)}
                        role="button"
                        aria-label={`View ${photo.alt}`}
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && setLightboxIndex(i)}
                    >
                        <img src={photo.src} alt={photo.alt} loading="lazy" />
                    </div>
                ))}
            </div>

            {isOpen && (
                <div className="lightbox-overlay" onClick={close}>
                    <button className="lightbox-close" onClick={close} aria-label="Close">&#x2715;</button>

                    {hasPrev && (
                        <button
                            className="lightbox-nav lightbox-prev"
                            onClick={(e) => { e.stopPropagation(); prev(); }}
                            aria-label="Previous photo"
                        >
                            &#8249;
                        </button>
                    )}

                    <img
                        src={photos[lightboxIndex].src}
                        alt={photos[lightboxIndex].alt}
                        className="lightbox-image"
                        onClick={(e) => e.stopPropagation()}
                    />

                    {hasNext && (
                        <button
                            className="lightbox-nav lightbox-next"
                            onClick={(e) => { e.stopPropagation(); next(); }}
                            aria-label="Next photo"
                        >
                            &#8250;
                        </button>
                    )}

                    <p className="lightbox-caption">{photos[lightboxIndex].alt}</p>
                </div>
            )}
        </>
    );
}

export default Gallery;
